import type { EnrollmentStep, MeDevicesResponse, OnboardingBriefing } from '@dyspatch/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, Copy, Laptop } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Rows } from '@/components/ui/section';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * A device name as somebody would type it, turned into one the hub will take.
 *
 * People name a laptop the way they say it out loud, "Sam MacBook Air", and the
 * hub's own pattern is lowercase with no spaces. Rejecting that sentence would
 * be correct and useless, so it is folded instead: lowercased, and every run of
 * anything that is not a letter or a digit becomes a single hyphen. Hyphens
 * only, because a name that reads back as `sam-macbook-air` is the one a person
 * will recognise in a fleet list, and a mixture of dots and underscores is not.
 *
 * Empty is a legitimate answer and means "you pick": the hub derives one from
 * the email, which is what happened for everybody before this field existed.
 */
export function deviceName(typed: string): string {
  return typed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
    .slice(0, 40);
}

/** Exported for the missing-checkout hint on a device's own card (issue #101): the same one-line copy-and-select box, for a command that names a repo rather than an enrollment step. */
export function CommandBox({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className="flex items-center gap-1">
      <pre className="bg-sunken dy-scroll min-w-0 flex-1 overflow-x-auto rounded-md border px-2 py-1.5 font-mono text-2xs select-all">
        {command}
      </pre>
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={copied ? 'Copied' : 'Copy this command'}
        onClick={() => {
          // A refused clipboard (no permission, an insecure origin) is not an
          // error to report: the command is on screen to select.
          void navigator.clipboard
            ?.writeText(command)
            .then(() => setCopied(true))
            .catch(() => undefined);
        }}
      >
        {copied ? <Check className="text-ok" /> : <Copy />}
      </Button>
    </div>
  );
}

/**
 * One instruction: its number, its title, whatever the hub had to say about it,
 * and the line to paste.
 *
 * The title and the hint share a line and wrap together rather than stacking
 * unconditionally, and the command sits straight under them. Four of these used
 * to run about 450px on their own, which is a screenful spent on a sequence a
 * person reads once, so the hint dropped to 11px and the row padding to roughly
 * a list row's. Nothing was dropped: the number still says how far along this
 * is, each fallback is still one click away, and the copy button still reports
 * that it copied. (There are three steps now rather than four, because the
 * worker is installed rather than cloned; the density is worth keeping anyway.)
 */
function StepRow({ step, index }: { step: EnrollmentStep; index: number }) {
  /** Which alternate is showing, by index, or null for the ordinary command. */
  const [alternate, setAlternate] = useState<number | null>(null);
  const alternates = step.alternates ?? [];
  const chosen = alternate === null ? null : (alternates[alternate] ?? null);
  const command = chosen ? chosen.command : step.command;

  return (
    <li className="flex gap-2.5 px-gutter py-2">
      <span className="border-line-2 text-fg-3 mt-px grid size-4.5 shrink-0 place-items-center rounded-full border text-2xs font-semibold">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <span className="text-foreground text-sm font-medium">{step.title}</span>
          {step.hint ? <span className="text-fg-3 min-w-0 text-2xs">{step.hint}</span> : null}
        </p>
        {command ? <CommandBox command={command} /> : null}
        {alternates.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {alternates.map((option, at) => (
              <button
                key={option.label}
                type="button"
                className={cn(
                  'text-2xs underline underline-offset-2',
                  at === alternate ? 'text-foreground' : 'text-fg-3 hover:text-foreground',
                )}
                onClick={() => setAlternate((current) => (current === at ? null : at))}
              >
                {at === alternate ? 'Back to the first one' : option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * What this laptop has to be before any of the steps below mean anything.
 *
 * Every one of these facts was configured on the hub and enforced on the
 * worker, and none of them was ever said here. The card's copy said "your own
 * subscription" and never which one; the only place a wrong account showed up
 * was a red badge in the admin's device table, after the fact. A missing
 * `HELIX_API_KEY` was a declined offer in an audit log hours later. And a
 * device is offered nothing for a repo that is not registered, which the
 * person connecting cannot see from here at all.
 *
 * Nothing is invented. Each line is drawn only for a fact the hub actually
 * holds, so a hub with no policy and no catalog draws no list rather than a
 * list of blanks, and reads exactly as it did before this existed.
 */
export function BeforeYouStart({ briefing }: { briefing: OnboardingBriefing }) {
  const { policy, toolVariables, repos } = briefing;
  const enabled = repos.filter((repo) => repo.enabled);

  const account = [
    policy.claudeOrg ? `the ${policy.claudeOrg} organization` : null,
    policy.accountDomain ? `an @${policy.accountDomain} address` : null,
  ].filter((part): part is string => part !== null);

  const lines: { key: string; text: ReactNode }[] = [];
  if (account.length > 0) {
    lines.push({
      key: 'claude',
      text: (
        <>
          Sign the agent in with a Claude account on {account.join(' and ')}. The worker refuses to
          start under any other, and it has its own login, separate from your{' '}
          <code className="font-mono text-2xs">~/.claude</code>.
        </>
      ),
    });
  }
  if (policy.githubOrg) {
    lines.push({
      key: 'gh',
      text: (
        <>
          Have <code className="font-mono text-2xs">gh</code> signed in as {policy.githubOrg}
          {enabled.length > 0
            ? `, with push access to ${enabled.map((repo) => repo.name).join(', ')}`
            : ''}
          . Read access is a real GitHub level: a run that cannot push does all the work and opens
          nothing.
        </>
      ),
    });
  }
  for (const entry of toolVariables) {
    lines.push({
      key: entry.variable,
      text: (
        <>
          Set <code className="font-mono text-2xs">{entry.variable}</code> in the shell that runs
          the worker. {entry.servers.join(' and ')} need{entry.servers.length === 1 ? 's' : ''} it,
          and a task that wants {entry.servers.length === 1 ? 'it' : 'them'} is declined without it.
        </>
      ),
    });
  }
  if (enabled.length > 0) {
    lines.push({
      key: 'repos',
      text: (
        <>
          Have {enabled.map((repo) => repo.name).join(', ')} cloned somewhere on this machine. Work
          is only ever offered for a repo the device has.
        </>
      ),
    });
  }

  if (lines.length === 0) return null;

  return (
    <div className="border-b px-gutter py-2">
      <p className="text-foreground text-sm font-medium">Before you start</p>
      <ul className="text-fg-3 mt-1 space-y-0.5 text-2xs">
        {lines.map((line) => (
          <li key={line.key} className="flex gap-1.5">
            <span aria-hidden className="text-fg-3 shrink-0">
              -
            </span>
            <span className="min-w-0">{line.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The sequence, drawn once.
 *
 * Both the member's card and the admin's invite composer show this, because
 * there is one way to join a fleet and two drawings of it drift. The admin form
 * used to print a single one-line paste instead, which could only carry one
 * remote form: it was SSH-only, and the first person handed it had no key
 * registered and no way, from inside the console, to find out that HTTPS was
 * there all along.
 *
 * Density is shared for the same reason the sequence is: whatever `StepRow`
 * costs in height, it costs in both places.
 */
export function EnrollmentSteps({ steps }: { steps: readonly EnrollmentStep[] }) {
  return (
    <Rows>
      {steps.map((step, index) => (
        <StepRow key={step.title} step={step} index={index} />
      ))}
    </Rows>
  );
}

/**
 * The name and the verb, on one line.
 *
 * These used to stack: a label, a field, a hint, and then the button under all
 * three, centred, which is four rows of a card for one short answer that is
 * optional anyway. Side by side they are one row plus the hint, and the hint is
 * the only line that changes as somebody types, so it is the only one that
 * needs its own.
 */
function NameAndConnect({
  name,
  onName,
  busy,
  error,
  onConnect,
  primary,
}: {
  name: string;
  onName: (next: string) => void;
  busy: boolean;
  error: string | null;
  onConnect: () => void;
  /** No device yet, so this is the thing to do rather than one more thing. */
  primary: boolean;
}) {
  const cleaned = deviceName(name);

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 space-y-1 sm:max-w-[15rem]">
          <Label htmlFor="cd-name">Name this device</Label>
          <Input
            id="cd-name"
            value={name}
            onChange={(event) => onName(event.target.value)}
            placeholder="sam-macbook-air"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <Button
          size="sm"
          variant={primary ? 'default' : 'outline'}
          disabled={busy}
          onClick={onConnect}
        >
          {busy ? 'Making your code…' : 'Connect this laptop'}
        </Button>
      </div>
      <p className="text-fg-3 text-2xs">
        {cleaned && cleaned !== name.toLowerCase()
          ? `It joins as ${cleaned}.`
          : 'Lowercase and hyphens. Leave it empty and the hub names it after your email.'}
      </p>
      {error ? (
        <p className="text-bad text-xs" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Connecting your own laptop, start to finish, without leaving the page.
 *
 * What this replaces was a sentence: "Ask an admin for an enrollment code."
 * There was no button under it, and nothing anywhere else in the console for a
 * member to add their own machine. Somebody new signed in, went to Agents,
 * read that, and came back to the dashboard having done nothing, which is
 * exactly what happened the first time anybody tried it.
 *
 * Three things make this finish rather than merely start:
 *
 *  - The instructions are real commands for this hub, composed by the hub. What
 *    the console used to hand out was `npx dyspatch-worker init`, and that
 *    package does not exist; anybody who pasted it watched it fail with no way
 *    to tell whether they had done something wrong.
 *  - It waits. The card polls until the hub has actually seen the device
 *    register under this person's account, and says so by name. A card that
 *    closes when you press Copy teaches nothing about whether it worked, and
 *    "did it work?" is the question every one of these flows dies on.
 *  - An expired code is one click, not another conversation. The code lives two
 *    hours because it is redeemed after a clone and an install, and when it does
 *    lapse the card mints another rather than sending somebody back to an admin.
 *
 * What it is shaped like, and why, after issue B-s94m. This used to be a centred
 * `py-12` hero under the device list: a 40px laptop, a 17px heading, a paragraph,
 * a stacked field, a button. About 560px of a 1480px screen for an action almost
 * nobody on that screen is taking, because almost everybody looking at Agents
 * already has an agent. So it now has three shapes rather than one:
 *
 *  - Somebody who already has a device gets a single row at the foot of the
 *    list, chevron first, with the shared-usage-window caveat as its meta. It is
 *    a list row like the ones above it, and it opens in place.
 *  - Somebody with no device at all still gets an empty state, because the word
 *    "agent" has to be explained once and this is the only screen that can. It
 *    is one sentence and one line of controls rather than a hero.
 *  - Anything the person needs to act on forces the block open regardless: a
 *    live invite, a lapsed code, or a refusal from the hub. Nobody who just
 *    pressed the button should have to reopen a disclosure to find the code
 *    they minted.
 */
export function ConnectDeviceCard() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<{
    workerName: string;
    steps: EnrollmentStep[];
    expiresAt: string;
  } | null>(null);

  // While a code is out, the card watches for the device to appear. One second
  // is not a burden on a hub that is already streaming, and the difference
  // between a two-second confirmation and a ten-second one is the difference
  // between "it worked" and "did it work?".
  const devices = useQuery<MeDevicesResponse>({
    queryKey: hubKeys.myDevices,
    queryFn: api.myDevices,
    refetchInterval: session ? 1_000 : false,
  });

  // What this hub requires of the laptop, which is three pieces of its own
  // configuration and changes about never. Failing is not an error state here:
  // the steps are still the steps, and a card that refuses to draw them
  // because a supplementary read failed is worse than one that says less.
  const briefing = useQuery<OnboardingBriefing>({
    queryKey: hubKeys.onboarding,
    queryFn: api.onboarding,
    staleTime: 5 * 60_000,
  });

  const connected = session
    ? (devices.data?.devices.find((device) => device.name === session.workerName) ?? null)
    : null;
  const pending = session
    ? (devices.data?.pending.find((invite) => invite.workerName === session.workerName) ?? null)
    : null;
  // Open, and the hub no longer lists it: either it lapsed or somebody revoked
  // it. Either way the answer is another code, not another conversation.
  const lapsed = Boolean(session && !connected && devices.data && !pending);

  const [name, setName] = useState('');
  const cleaned = deviceName(name);
  // Collapsed by default, and only ever consulted on the disclosure below: the
  // live invite, the lapsed code and the connected device each draw themselves
  // instead, so they are open whatever this says.
  const [open, setOpen] = useState(false);

  const connect = useHubAction(
    async () => {
      const made = await api.connectMyDevice(cleaned || undefined);
      setSession({
        workerName: made.invite.workerName,
        steps: made.steps,
        expiresAt: made.invite.expiresAt,
      });
      return made;
    },
    { inlineError: true },
  );

  // The device landing is a fleet-wide fact, not just this card's: the
  // dashboard's checklist and its Agents tile are both wrong until they hear.
  useEffect(() => {
    if (!connected) return;
    void queryClient.invalidateQueries();
  }, [connected, queryClient]);

  // The same button either way, but not the same sentence: telling somebody
  // who has two machines connected that they have no agent is how a working
  // page reads as a broken one. It is also what decides the hairline, which is
  // spent only where this block genuinely touches a device row above it.
  const first = !devices.data?.devices.length;
  const edge = first ? undefined : 'border-t';

  // Putting the invite away puts the disclosure away with it, so finishing
  // leaves the list the height it was before anybody pressed anything.
  const dismiss = () => {
    setSession(null);
    setOpen(false);
  };

  if (connected) {
    return (
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-2.5 gap-y-1 px-gutter py-[calc(var(--dy-row-py)+1px)]',
          edge,
        )}
      >
        <Check className="text-ok size-4 shrink-0" strokeWidth={3} aria-hidden />
        <span className="text-foreground text-base font-medium">
          {connected.name} is connected
          {connected.hostname ? ` on ${connected.hostname}` : ''}
        </span>
        <span className="text-fg-3 min-w-0 text-xs">
          Leave <code className="font-mono text-2xs">start</code> running in that terminal and it
          takes work.
        </span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={dismiss}>
          Done
        </Button>
      </div>
    );
  }

  if (session) {
    return (
      <div className={edge}>
        <div className="flex flex-wrap items-baseline gap-x-2 border-b px-gutter py-2">
          <p className="text-foreground text-sm font-medium">
            Run these on the laptop you want to connect
          </p>
          <p className="text-fg-3 min-w-0 text-2xs">
            It joins as <span className="font-mono">{session.workerName}</span>. Leave this page
            open and it says so here the moment the hub sees it.
          </p>
        </div>

        {briefing.data ? <BeforeYouStart briefing={briefing.data} /> : null}
        <EnrollmentSteps steps={session.steps} />

        <div
          className={cn(
            'flex flex-wrap items-center gap-2 border-t px-gutter py-2',
            lapsed ? 'bg-sunken' : null,
          )}
        >
          {lapsed ? (
            <>
              <span className="text-warn text-xs">
                That code has expired. Nothing is lost; take a fresh one.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                disabled={connect.busy}
                onClick={() => connect.run()}
              >
                New code
              </Button>
            </>
          ) : (
            <>
              <span className="border-line-2 size-2.5 animate-pulse rounded-full border-2" />
              <span className="text-fg-2 text-xs">
                Waiting for {session.workerName} to check in. This page is watching.
              </span>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={dismiss}>
                Do this later
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (first) {
    // The one screen that can say what an agent is, so it still does, at the
    // one moment a person has no other way to find out. Trimmed to a sentence
    // and a line of controls: an empty state, not a hero.
    return (
      <div className="px-gutter py-8">
        <div className="flex items-center gap-2">
          <Laptop className="text-fg-3 size-4 shrink-0" strokeWidth={1.5} aria-hidden />
          <p className="text-foreground text-md font-medium">No agent on your machine yet</p>
        </div>
        <p className="text-fg-3 mt-1 mb-3 max-w-xl text-sm">
          An agent is Claude Code running on your own laptop, under your own subscription; until you
          connect one, somebody else's machine does the work.
        </p>
        <NameAndConnect
          name={name}
          onName={setName}
          busy={connect.busy}
          error={connect.error}
          onConnect={() => connect.run()}
          primary
        />
      </div>
    );
  }

  // A refusal from the hub holds the disclosure open on its own, so a sentence
  // nobody has read yet cannot be collapsed out of sight by the mint that is
  // still in flight when somebody closes the row. Collapsing on purpose clears
  // it, because otherwise the chevron would turn and the row would not move.
  const expanded = open || connect.error !== null;

  return (
    <div className={edge}>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => {
          if (expanded) connect.reset();
          setOpen(!expanded);
        }}
        className="hover:bg-raised/60 flex w-full items-center gap-2 px-gutter py-[calc(var(--dy-row-py)+1px)] text-left transition-colors duration-100"
      >
        <ChevronRight
          className={cn(
            'text-fg-3 size-3 shrink-0 transition-transform duration-100',
            expanded && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="shrink-0 text-base font-medium whitespace-nowrap">
          Connect another machine
        </span>
        <span className="text-fg-3 min-w-0 truncate text-xs max-sm:hidden">
          shares your usage window and concurrency budget, so it adds hours rather than headroom
        </span>
      </button>
      {expanded ? (
        <div className="px-gutter pb-3">
          <NameAndConnect
            name={name}
            onName={setName}
            busy={connect.busy}
            error={connect.error}
            onConnect={() => connect.run()}
            primary={false}
          />
        </div>
      ) : null}
    </div>
  );
}
